import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Button, Field, Card, Modal, StatusPill } from "@/components/kit";
import ConfirmDelete from "@/components/ConfirmDelete";

const INP = "w-full bg-warm-cream border border-ink/20 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px] focus:outline-none focus:border-dark-olive/50";

const INVITE_TEMPLATES = [
  { id: "welcome", label: "Warm welcome", subject: "You're invited to join Rosemoor",
    body: (n) => `Hi ${n},\n\nWe'd be thrilled to count you as a full member of Rosemoor. It only takes a minute — just tap the button below and your name and email are already filled in.` },
  { id: "back", label: "Come back", subject: "We'd love to have you back at Rosemoor",
    body: (n) => `Hi ${n},\n\nIt's been a while! We'd love to welcome you back as a Rosemoor member. Tap below to set up your account — we've pre-filled your details.` },
  { id: "nudge", label: "Quick nudge", subject: "Become a Rosemoor member",
    body: (n) => `Hi ${n},\n\nReady to make it official? Create your Rosemoor membership in under a minute — your name and email are already filled in.` },
];

function ContactForm({ initial, onClose, onDone }) {
  const [f, setF] = useState({ name: initial?.name || "", phone: initial?.phone || "", email: initial?.email || "", organization: initial?.organization || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("A name is required"); return; }
    setBusy(true);
    try {
      if (initial?.contact_id) await api.put(`/contacts/${initial.contact_id}`, f);
      else await api.post("/contacts", f);
      toast.success(initial?.contact_id ? "Contact updated" : "Contact added");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={initial?.contact_id ? "Edit contact" : "New contact"}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button data-testid="contact-save" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Button></>}>
      <div className="space-y-3">
        <Field label="Name"><input data-testid="contact-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={INP} /></Field>
        <Field label="Organization"><input data-testid="contact-org" value={f.organization} onChange={(e) => setF({ ...f, organization: e.target.value })} className={INP} /></Field>
        <Field label="Phone"><input data-testid="contact-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={INP} /></Field>
        <Field label="Email"><input data-testid="contact-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={INP} /></Field>
      </div>
    </Modal>
  );
}

function InviteForm({ contact, onClose, onDone }) {
  const first = (contact.name || "there").split(" ")[0];
  const [tpl, setTpl] = useState("welcome");
  const [subject, setSubject] = useState(INVITE_TEMPLATES[0].subject);
  const [message, setMessage] = useState(INVITE_TEMPLATES[0].body(first));
  const [busy, setBusy] = useState(false);
  const pick = (t) => { setTpl(t.id); setSubject(t.subject); setMessage(t.body(first)); };
  const send = async () => {
    setBusy(true);
    try { await api.post(`/visitors/${contact.contact_id}/invite`, { subject, message, origin: window.location.origin }); toast.success("Invitation emailed"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send invite"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Invite ${contact.name} to join`} data-testid="invite-form"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button data-testid="invite-send" onClick={send} disabled={busy}>{busy ? "Sending…" : "Send invitation"}</Button></>}>
      {!contact.email && <p className="text-[13px] text-maroon mb-3">No email on file — add one first.</p>}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="invite-templates">
        {INVITE_TEMPLATES.map((t) => (
          <button key={t.id} onClick={() => pick(t)} className={`text-[13px] px-3 py-1.5 rounded-full border ${tpl === t.id ? "bg-dark-olive text-cream border-dark-olive" : "border-ink/20"}`}>{t.label}</button>
        ))}
      </div>
      <Field label="Subject"><input value={subject} onChange={(e) => setSubject(e.target.value)} className={INP} /></Field>
      <div className="mt-3"><Field label="Message"><textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} className={INP} /></Field></div>
    </Modal>
  );
}

function ConvertForm({ contact, onClose, onDone }) {
  const [f, setF] = useState({ name: contact.name || "", email: contact.email || "", phone: contact.phone || "", dob: "", account_type: "resident", street: "", city: "", state: "", zip: "", pin: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/membership/members", { ...f });
      const m = r.data.member || r.data;
      await api.post(`/visitors/${contact.contact_id}/converted`, { member_id: m.member_id });
      toast.success(`Created ${m.member_number}`); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create member"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Convert to member" data-testid="convert-form"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button data-testid="convert-save" onClick={save} disabled={busy}>{busy ? "Creating…" : "Create member"}</Button></>}>
      <p className="text-[13px] text-ink/60 mb-3">Their name and contact carry over — add the details a membership needs.</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name"><input data-testid="convert-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={INP} /></Field>
        <Field label="Account type"><select data-testid="convert-type" value={f.account_type} onChange={(e) => setF({ ...f, account_type: e.target.value })} className={INP}><option value="resident">Resident</option><option value="business">Business</option><option value="organization">Organization</option><option value="church">Church</option></select></Field>
        <Field label="Email"><input data-testid="convert-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={INP} /></Field>
        <Field label="Phone"><input data-testid="convert-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={INP} /></Field>
        <Field label="Date of birth"><input data-testid="convert-dob" type="date" value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} className={INP} /></Field>
        <Field label="Street"><input value={f.street} onChange={(e) => setF({ ...f, street: e.target.value })} className={INP} /></Field>
        <Field label="City"><input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className={INP} /></Field>
        <Field label="State"><input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} className={INP} /></Field>
        <Field label="ZIP"><input value={f.zip} onChange={(e) => setF({ ...f, zip: e.target.value })} className={INP} /></Field>
        <Field label="4-digit PIN"><input data-testid="convert-pin" inputMode="numeric" maxLength={4} value={f.pin} onChange={(e) => setF({ ...f, pin: e.target.value.replace(/\D/g, "") })} className={INP} /></Field>
      </div>
    </Modal>
  );
}

export default function Contacts() {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [form, setForm] = useState(null);
  const [invite, setInvite] = useState(null);
  const [convert, setConvert] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => { api.get("/contacts", { params: { q, limit: 200 } }).then((r) => setData(r.data)).catch(() => {}); }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  return (
    <div data-testid="contacts-page">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h1 className="font-zilla text-3xl font-semibold">Contacts</h1>
          <p className="text-[15px] text-ink/60 max-w-xl">People RCA wants to bring back in as members — old lists and lapsed contacts. Invite them to join or convert them to a member.</p>
        </div>
        <Button data-testid="new-contact" onClick={() => setForm({})}><Icon name="UserPlus" className="w-4 h-4" /> New contact</Button>
      </div>
      <input data-testid="contacts-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email, organization" className="w-full bg-cream border border-ink/20 rounded-lg px-4 py-2.5 min-h-[44px] text-[15px] mb-4" />
      <Card className="p-0 overflow-hidden">
        {data.items.length === 0 && <div className="p-10 text-center text-ink/55" data-testid="contacts-empty">No contacts yet. Add one with “New contact”.</div>}
        {data.items.map((c) => (
          <div key={c.contact_id} data-testid={`contact-${c.contact_id}`} className="flex items-center gap-3 px-4 py-3 border-b border-ink/8 last:border-0 hover:bg-warm-cream/60 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-[15px] truncate flex items-center gap-2">{c.name}{c.organization && <span className="text-ink/50 font-normal"> · {c.organization}</span>}
                {c.converted && <StatusPill tone="olive">Joined</StatusPill>}
                {!c.converted && c.invited_at && <StatusPill tone="gold">Invited</StatusPill>}
              </div>
              <div className="text-[13px] text-ink/55 truncate">{[c.email, c.phone].filter(Boolean).join(" · ") || "—"}{c.source ? ` · via ${c.source}` : ""}</div>
            </div>
            {!c.converted && <button data-testid={`invite-contact-${c.contact_id}`} onClick={() => setInvite(c)} disabled={!c.email} title={c.email ? "" : "Add an email first"} className="text-[13px] font-semibold text-dark-olive hover:underline disabled:opacity-40 shrink-0">Invite</button>}
            {!c.converted && <button data-testid={`convert-contact-${c.contact_id}`} onClick={() => setConvert(c)} className="text-[13px] font-semibold text-maroon hover:underline shrink-0">Convert</button>}
            <button data-testid={`edit-contact-${c.contact_id}`} onClick={() => setForm(c)} className="text-[13px] font-semibold text-ink/70 hover:underline shrink-0">Edit</button>
            <button data-testid={`delete-contact-${c.contact_id}`} onClick={() => setDel(c)} className="text-[13px] font-semibold text-maroon/80 hover:underline shrink-0">Delete</button>
          </div>
        ))}
      </Card>
      <div className="text-[13px] text-ink/45 mt-2">{data.total} contact{data.total === 1 ? "" : "s"}</div>
      {form && <ContactForm initial={form.contact_id ? form : null} onClose={() => setForm(null)} onDone={() => { setForm(null); load(); }} />}
      {invite && <InviteForm contact={invite} onClose={() => setInvite(null)} onDone={() => { setInvite(null); load(); }} />}
      {convert && <ConvertForm contact={convert} onClose={() => setConvert(null)} onDone={() => { setConvert(null); load(); }} />}
      <ConfirmDelete open={!!del} onClose={() => setDel(null)} onConfirm={async () => { await api.delete(`/contacts/${del.contact_id}`); toast.success("Contact moved to Trash"); load(); }} title="Delete this contact?" message="This moves the contact to Trash, where it can be restored for 30 days before it is permanently removed." />
    </div>
  );
}
